import { EmailHistory } from '../email/EmailHistory';
import AssignmentReturnRounded from '@mui/icons-material/AssignmentReturnRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { Money } from '../../components/Money';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, dateTime, qty as fmtQty } from '../../lib/format';
import { salesReturnDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useShop } from '../../shop/ShopProvider';
import { links } from '../payments/kit/labels';
import {
  BackLink,
  DetailSkeleton,
  ErrorState,
  InfoGrid,
  SectionCard,
  StatCard,
  StatGrid,
} from '../payments/kit/ui';
import { keys } from './api';
import { MoreMenu } from '../../components/MoreMenu';
import { linkedKnown } from '../../components/useLinked';

export function SalesReturnDetailPage() {
  const { id = '' } = useParams();
  const { format } = useShop();
  const q = useQuery({
    queryKey: keys.salesReturn(id),
    queryFn: () => unwrap(api.GET('/sales-returns/{id}', { params: { path: { id } } })),
  });

  if (q.isLoading) return <DetailSkeleton />;
  if (q.isError || !q.data)
    return (
      <>
        <BackLink to="/returns" label="Returns & notes" />
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const r = q.data;
  const restocked = r.lines.filter((l) => l.location && !l.writtenOff).length;
  const damaged = r.lines.filter((l) => l.condition === 'DAMAGED').length;

  return (
    <>
      <BackLink to="/returns" label="Returns & notes" />
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h3">{r.number}</Typography>
          <StatusChip status={r.status} />
          <Chip size="small" variant="outlined" label="Sales return" />
          <Box sx={{ flex: 1 }} />
          <MoreMenu
            linked={[
              linkedKnown('invoices', [r.salesInvoice], 'Invoice'),
              linkedKnown('creditNotes', [r.creditNote], 'Credit note'),
            ]}
            exportModel="sales_return"
            exportId={r.id}
          />
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          <Link
            component={RouterLink}
            to={links.contact(r.contact.id, 'customer')}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {r.contact.name}
          </Link>{' '}
          · from{' '}
          <Link
            component={RouterLink}
            to={links.salesInvoice(r.salesInvoice.id)}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {r.salesInvoice.number}
          </Link>{' '}
          · {businessDate(r.docDate, format)}
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            '&:empty': { display: 'none' },
          }}
        >
          <WhatsappA4
            type="sales_return"
            id={r.id}
            number={r.number}
            contact={r.contact}
            vars={{ date: businessDate(r.docDate, format) }}
            doc={() => salesReturnDoc(r, format)}
          />
        </Box>
      </Box>

      <StatGrid>
        <StatCard
          label="Credit note"
          value={r.creditNote ? <Money value={r.creditNote.total} /> : '—'}
          hint={
            r.creditNote ? (
              <Link component={RouterLink} to={links.creditNote(r.creditNote.id)} underline="hover">
                {r.creditNote.number}
              </Link>
            ) : (
              'No credit issued'
            )
          }
          icon={<ReceiptLongRounded />}
          tone="primary"
        />
        <StatCard
          label="Lines"
          value={r.lines.length}
          hint={`${String(restocked)} back in stock`}
          icon={<AssignmentReturnRounded />}
          tone="info"
        />
        <StatCard
          label="Back in stock"
          value={r.totalCost !== null ? <Money value={r.totalCost} /> : '—'}
          hint={r.totalCost !== null ? 'At the original cost' : 'Cost is hidden for your role'}
          icon={<Inventory2Rounded />}
          tone="success"
        />
        <StatCard
          label="Written off"
          value={r.writeOffCost !== null ? <Money value={r.writeOffCost} /> : '—'}
          hint={`${String(damaged)} damaged line${damaged === 1 ? '' : 's'}`}
          icon={<WarningAmberRounded />}
          tone={damaged ? 'warning' : 'neutral'}
        />
      </StatGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1.6fr 1fr' },
          alignItems: 'start',
        }}
      >
        <SectionCard flush title="Returned items" subtitle="Where each line went">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    Condition
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Went to</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    Cost
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {r.lines.map((l) => (
                  <TableRow key={l.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {l.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" className="tabular">
                      {fmtQty(l.qty, format)} {l.unitSymbol}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={l.condition === 'DAMAGED' ? 'warning' : 'success'}
                        label={l.condition === 'DAMAGED' ? 'Damaged' : 'Resalable'}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}
                    >
                      {l.location
                        ? `${l.location.name}${l.writtenOff ? ' · written off' : ''}`
                        : 'Credit only'}
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {l.totalCost !== null ? <Money value={l.totalCost} /> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
        <SectionCard title="Details">
          <InfoGrid
            items={[
              { label: 'Customer', value: r.contact.name },
              { label: 'Invoice', value: r.salesInvoice.number },
              { label: 'Date', value: businessDate(r.docDate, format) },
              { label: 'Reason', value: r.reason ?? '—' },
              { label: 'Posted', value: dateTime(r.postedAt, format) },
            ]}
          />
          {r.notes && (
            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '10px', bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {r.notes}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2.5 }}>
            Returns are final. To refund the customer, open the credit note.
          </Typography>
        </SectionCard>
      </Box>
      <AttachmentsPanel recordType="SALES_RETURN" recordId={r.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="sales_return" documentId={r.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="sales_return" documentId={r.id} sx={{ mt: 3 }} />
    </>
  );
}
